import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';
import { GroupDto } from './groupSlice';
import { blankReferenceYearDto, ReferenceYearDto } from './referenceYearSlice';
import { IAutoCompleteEmployeeDto } from '../../interfaces/IAutoCompleteEmployeeDto';

export interface ObjectiveEmployeeExceptionDto {
    [key: string]: any;
    id: number;
    employeeExceptionId: number;
    objectiveId: number;
    objectiveNameFr: string;
    objectiveNameEn: string;
    weightOriginal: number;
    weightManual: number;
    hasThatObjective: boolean;
    isDeleted: boolean;
}
export interface GetByDepartmentDto {
    departmentId: number;
    referenceYearId: number;
}

export interface AutoCompleteDto {
    searchValue: string;
    referenceYearId: number;
}

export interface EmployeeExceptionDto {
    [key: string]: any;
    id: number;
    isDeleted: boolean;
    employeeId: string;
    employeeName: string;
    employeeNumber: string;
    internalNote: string;
    originalEligibility: boolean;
    manualEligibility?: boolean;

    targetOriginal: number;
    targetManual?: number;

    objectiveQuantityMinOriginal: number;
    objectiveQuantityMinManual?: number;
    objectiveQuantityMaxOriginal: number;
    objectiveQuantityMaxManual?: number;

    hrLevelOriginalId: number;
    hrLevelManualId?: number;

    businessCategoryOriginalId: number;
    businessCategoryManualId?: number;

    groupOriginalId: number;
    groupManualId?: number;

    departmentOriginalId: number;
    departmentManualId?: number;

    referenceYearId: number;
    referenceYear?: ReferenceYearDto;

    objectiveEmployeeExceptions: ObjectiveEmployeeExceptionDto[];
}

export interface EmployeeExceptionState {
    employeeExceptions: EmployeeExceptionDto[];
    employeeException: EmployeeExceptionDto;
    gettingEmployeeExceptions: boolean;
    gettingEmployeeException: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    getEmployeeExceptionstatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    gettingGroupByDepartment: boolean;
    statusByDepartment: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
    availableGroupForDepartment?: GroupDto;
    gettingAutoCompleteEmployees: boolean;
    archivingEmployeeException: boolean;
    gettingAutoCompleteEmployeePicture: boolean;
    gettingAutoCompleteEmployeeStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    gettingAutoCompleteEmployeePictureStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    autoCompleteEmployees?: IAutoCompleteEmployeeDto[];
    autocompletedEmployee?: IAutoCompleteEmployeeDto;
    autocompletedEmployeePicture: string;
}

export const blankEmployeeException: EmployeeExceptionDto = {
    id: 0,
    isDeleted: false,
    employeeId: '',
    employeeName: '',
    employeeNumber: '',
    internalNote: '',
    originalEligibility: false,
    manualEligibility: undefined,

    targetOriginal: 0,
    targetManual: 0,

    objectiveQuantityMinOriginal: 0,
    objectiveQuantityMinManual: 0,
    objectiveQuantityMaxOriginal: 0,
    objectiveQuantityMaxManual: 0,

    hrLevelOriginalId: 0,
    hrLevelManualId: undefined,

    businessCategoryOriginalId: 0,
    businessCategoryManualId: undefined,

    groupOriginalId: 0,
    groupManualId: undefined,

    departmentOriginalId: 0,
    departmentManualId: undefined,

    referenceYearId: 0,
    referenceYear: blankReferenceYearDto,

    objectiveEmployeeExceptions: [],
};

const initialState: EmployeeExceptionState = {
    employeeExceptions: [],
    employeeException: blankEmployeeException,
    gettingEmployeeExceptions: false,
    gettingEmployeeException: false,
    status: 'idle',
    getEmployeeExceptionstatus: 'idle',
    error: undefined,
    gettingGroupByDepartment: false,
    archivingEmployeeException: false,
    statusByDepartment: 'idle',
    gettingAutoCompleteEmployees: false,
    gettingAutoCompleteEmployeePicture: false,
    gettingAutoCompleteEmployeeStatus: 'idle',
    gettingAutoCompleteEmployeePictureStatus: 'idle',
    autoCompleteEmployees: [],
    autocompletedEmployeePicture: '',
};

export const employeeExceptionSlice = createSlice({
    name: 'employeeExceptions',
    initialState,
    reducers: {
        clearEmployeeExceptions: (state: EmployeeExceptionState) => {
            state.employeeException = { ...blankEmployeeException };
            state.autoCompleteEmployees = [];
            state.autocompletedEmployee = undefined;
            state.autocompletedEmployeePicture = '';
        },
        updateEmployeeException: (state: EmployeeExceptionState, param) => {
            const { payload } = param;
            state.employeeException = payload;
        },
        updateObjectiveEmployeeException: (state, param) => {
            const { payload } = param;
            const objectiveEmployeeException = state.employeeException.objectiveEmployeeExceptions.find(
                (x: ObjectiveEmployeeExceptionDto) => x.id === payload.id
            );

            if (objectiveEmployeeException) {
                objectiveEmployeeException[payload.fieldName as keyof ObjectiveEmployeeExceptionDto] = payload.value;
            }
        },
        setAutoCompletedEmployee: (state, param) => {
            const { payload } = param;
            state.autocompletedEmployee = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getEmployeeExceptions.pending, (state, action) => {
                state.gettingEmployeeExceptions = true;
                state.status = 'loading';
            })
            .addCase(getEmployeeExceptions.fulfilled, (state, action) => {
                state.gettingEmployeeExceptions = false;
                state.employeeExceptions = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getEmployeeExceptions.rejected, (state, action) => {
                state.gettingEmployeeExceptions = false;
                state.employeeExceptions = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getEmployeeException.pending, (state, action) => {
                state.gettingEmployeeException = true;
                state.getEmployeeExceptionstatus = 'loading';
            })
            .addCase(getEmployeeException.fulfilled, (state, action) => {
                state.gettingEmployeeException = false;
                state.employeeException = action.payload;
                state.autocompletedEmployee = { name: action.payload.employeeName, id: action.payload.employeeNumber };
                state.getEmployeeExceptionstatus = 'succeeded';
            })
            .addCase(getEmployeeException.rejected, (state, action) => {
                state.gettingEmployeeException = false;
                state.employeeException = blankEmployeeException;
                state.getEmployeeExceptionstatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getEmployeePicture.pending, (state, action) => {
                state.gettingAutoCompleteEmployeePicture = true;
                state.gettingAutoCompleteEmployeePictureStatus = 'loading';
            })
            .addCase(getEmployeePicture.fulfilled, (state, action) => {
                state.gettingAutoCompleteEmployeePicture = false;
                if (!action.payload || action.payload === '') {
                    state.autocompletedEmployeePicture = '/images/empty-profile.jpg';
                } else {
                    state.autocompletedEmployeePicture = action.payload;
                }
                state.gettingAutoCompleteEmployeePictureStatus = 'succeeded';
            })
            .addCase(getEmployeePicture.rejected, (state, action) => {
                state.gettingAutoCompleteEmployeePicture = false;
                state.autocompletedEmployeePicture = '/images/empty-profile.jpg';
                state.gettingAutoCompleteEmployeePictureStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getAutoCompleteEmployee.pending, (state, action) => {
                state.gettingAutoCompleteEmployees = true;
                state.gettingAutoCompleteEmployeeStatus = 'loading';
            })
            .addCase(getAutoCompleteEmployee.fulfilled, (state, action) => {
                state.gettingAutoCompleteEmployees = false;
                state.autoCompleteEmployees = action.payload;
                state.gettingAutoCompleteEmployeeStatus = 'succeeded';
            })
            .addCase(getAutoCompleteEmployee.rejected, (state, action) => {
                state.gettingAutoCompleteEmployees = false;
                state.autoCompleteEmployees = [];
                state.gettingAutoCompleteEmployeeStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getGroupByDepartment.pending, (state, action) => {
                state.gettingGroupByDepartment = true;
                state.statusByDepartment = 'loading';
            })
            .addCase(getGroupByDepartment.fulfilled, (state, action) => {
                state.gettingGroupByDepartment = false;
                state.availableGroupForDepartment = action.payload;
                state.statusByDepartment = 'succeeded';
            })
            .addCase(getGroupByDepartment.rejected, (state, action) => {
                state.gettingGroupByDepartment = false;
                state.availableGroupForDepartment = undefined;
                state.statusByDepartment = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveEmployeeException.pending, (state, action) => {
                state.archivingEmployeeException = true;
                state.status = 'loading';
            })
            .addCase(archiveEmployeeException.fulfilled, (state, action) => {
                state.archivingEmployeeException = false;
                state.employeeExceptions = state.employeeExceptions.filter((x) => x.id !== action.meta.arg);
                state.status = 'succeeded';
            })
            .addCase(archiveEmployeeException.rejected, (state, action) => {
                state.archivingEmployeeException = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearEmployeeExceptions, updateEmployeeException, updateObjectiveEmployeeException, setAutoCompletedEmployee } =
    employeeExceptionSlice.actions;

export const getEmployeeExceptions = createAsyncThunk(
    'employeeExceptions/getEmployeeExceptions',
    async (referenceYearId: number, thunkAPI) => {
        const response = await axios.get(`/api/synergy/employee-exceptions`, { params: { referenceYearId } });

        return response.data;
    }
);

export const getEmployeePicture = createAsyncThunk('employeeExceptions/getEmployeePicture', async (username: string, thunkAPI) => {
    const response = await axios.get(`/api/employee/get-picture-by-username/${username}`);

    return response.data;
});

export const getEmployeeException = createAsyncThunk('employeeExceptions/getEmployeeException/1', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/employee-exceptions/${id}`);

    return response.data;
});

export const getGroupByDepartment = createAsyncThunk('groups/getGroupByDepartment', async (params: GetByDepartmentDto, thunkAPI) => {
    const response = await axios.get<GroupDto>(`/api/synergy/groups/get-by-department/${params.departmentId}/${params.referenceYearId}`);

    const { data } = response;

    return data;
});

export const getAutoCompleteEmployee = createAsyncThunk(
    'employeeExceptions/getAutoCompleteEmployee',
    async (search: AutoCompleteDto, thunkAPI) => {
        if (search.searchValue && search.searchValue !== '') {
            const response = await axios.get(`/api/synergy/employee-exceptions/autocomplete/${search.searchValue}`);
            return response.data;
        }
        return [];
    }
);

export const archiveEmployeeException = createAsyncThunk('employeeExceptions/archiveEmployeeException', async (id: number, thunkAPI) => {
    return axios
        .post(`/api/synergy/employee-exceptions/archive/${id}`)
        .then((data) => {
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('archivedEmployeeException'),
                })
            );
            return data;
        })
        .catch(async (error) => {
            const message = await error.response.data.text();
            thunkAPI.dispatch(
                showMessage({
                    type: 'error',
                    message: i18n.t(message),
                })
            );
        });
});

export const saveEmployeeException = createAsyncThunk(
    'employeeExceptions/saveEmplopoyeeException',
    async (employeeExceptionToSave: EmployeeExceptionDto, thunkAPI) => {
        if (employeeExceptionToSave.id === 0) {
            return axios
                .post(`/api/synergy/employee-exceptions/create`, employeeExceptionToSave)
                .then((data) => {
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'info',
                            message: getAppText('createEmployeeException'),
                        })
                    );
                    return data;
                })
                .catch(async (error) => {
                    const message = await error.response.data.errors.map((msg: any) => msg.summary).join( ', ');
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'error',
                            message: message,
                        })
                    );
                });
        } else {
            return axios
                .put(`/api/synergy/employee-exceptions/${employeeExceptionToSave.id}`, employeeExceptionToSave)
                .then((data) => {
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'info',
                            message: getAppText('saveEmployeeException'),
                        })
                    );
                    return data;
                })
                .catch(async (error) => {
                    const message = await error.response.data.text();
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'error',
                            message: i18n.t(message),
                        })
                    );
                });
        }
    }
);
